import { isEqual } from 'lodash';
import { type RecordId } from '../types';
import type ClientBase from './ClientBase';
import { type IDataStoreRecordBehaviors } from './DataStoreRecordBehaviors';

// A DataStore represents a table-like structure that is collaboratively
// synchronized between clients. It is a very generic table where each
// `object` is associated with an `id` (which is just a unique string) and
// has any number of properties according to the limitations of ot-whiteboards.
// Generally this means that property values should be scalars.
//
// Each object which is added to the DataStore must implement
// the IDataStoreRecordBehaviors interface, and their IDs must be unique.
class DataStore<T extends IDataStoreRecordBehaviors = any> {
  _tableKey: string;
  _client: ClientBase | null;
  _records: { [id: RecordId]: T } = {};

  // Create a store that will store data at `tableKey` in the serialized
  // data.
  constructor(tableKey: string, client: ClientBase | null) {
    this._tableKey = tableKey;
    this._client = client;
  }

  // Called after the data store is loaded (restored from document)
  loaded() {}

  addRecord(record: T) {
    const recordId = record.uniqueId();
    if (this._records[recordId]) {
      const errorMessage = this.makeDuplicateRecordErrorMessage(record);
      throw new Error(
        `Record already exists in '${this._tableKey}' DataStore with id ${recordId}. ${errorMessage}`
      );
    }
    record._dataStore = this;
    this._records[recordId] = record;
    this._client?.addObject(this._tableKey, record);
    return record;
  }

  removeRecord(record: T) {
    record._dataStore = undefined;
    delete this._records[record.uniqueId()];
    this._client?.removeObject(this._tableKey, record);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  recordDidUpdate(record: T) {}

  findRecordById(id: RecordId) {
    return this._records[id];
  }

  records() {
    return Object.values(this._records);
  }

  clear() {
    this._records = {};
  }

  private makeDuplicateRecordErrorMessage(newRecord: T) {
    const recordId = newRecord.uniqueId();
    const messages: string[] = [];
    const initialData = this._client?.controller.props.initialData;
    if (initialData) {
      messages.push('`initialData` was passed as a prop');
      const foundObject = initialData[recordId];
      if (foundObject) {
        messages.push(
          'Object with same ID was found in `initialData`. Object in `initialData` and new object have the same JSON: ' +
            isEqual(initialData[recordId], newRecord.toJSON())
        );
      } else {
        messages.push('Object was not in `initialData`');
      }
    } else {
      messages.push('`initialData` prop was: ' + `${initialData}`);
    }
    messages.push(
      `Object in datastore and new object have the same JSON: ${isEqual(
        newRecord.toJSON(),
        this._records[recordId].toJSON()
      )}`
    );

    return messages.join('. ');
  }
}

export default DataStore;
